/**
 * CanvasSelectionBounds — a hairline box around everything currently selected.
 *
 * Replaces ReactFlow's own <NodesSelection> rect, which is unusable here for
 * two reasons. It is gated on `nodesSelectionActive`, a flag ReactFlow raises
 * from its OWN Partial candidate count rather than from what is actually
 * selected, so it appeared and vanished out of step with the node highlights.
 * And being interactive, it swallowed clicks meant for the nodes beneath it
 * (why d56ee3e80 suppressed it originally).
 *
 * This one is derived straight from `node.selected`, so it cannot disagree
 * with the highlights.
 *
 * It IS a handle. An earlier revision made it `pointer-events: none` and
 * justified that with "dragging any one selected node still moves the whole
 * selection, so nothing is lost" — which was wrong about what a bounding box
 * is for. With both this overlay and ReactFlow's suppressed native rect
 * ignoring the pointer, a drag starting on the box fell through to the pane:
 * under the Select tool that began a fresh marquee and discarded the
 * selection, and under Hand it panned the canvas.
 *
 * Re-enabling ReactFlow's own rect would not have covered it. That rect is
 * gated on `nodesSelectionActive`, which the Pane raises only in its
 * `onMouseUp` — so it exists after a marquee but never after a cmd-click
 * selection.
 *
 * Dragging routes through the store's `updateNodePositions`, the same call
 * ReactFlow's node drag uses, so position changes reach `onNodesChange` and
 * persist to YDoc exactly as a normal drag does. Writing to `setNodes`
 * instead would move the nodes on screen and silently fail to save them.
 *
 * Shown only for multi-selections: a box hugging a single node just traces
 * the highlight that node already draws.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStore, useStoreApi, type NodeDragItem } from 'reactflow'

/** Minimum selected nodes before the box is worth drawing. */
export const SELECTION_BOUNDS_MIN_NODES = 2

/** Breathing room in flow units so the box clears node borders. */
const PADDING = 8

export interface SelectionBoundsNode {
  selected?: boolean
  hidden?: boolean
  width?: number | null
  height?: number | null
  position: { x: number; y: number }
  positionAbsolute?: { x: number; y: number }
  /** True when other nodes name this one as their parent. */
  isGroup?: boolean
}

export interface SelectionBounds {
  x: number
  y: number
  width: number
  height: number
  count: number
}

/**
 * Tight bounds of the selected, measured, visible nodes — or null when there
 * is nothing worth boxing.
 *
 * A single selected node normally needs no box: its own highlight already
 * traces it. A GROUP is the exception and always gets one. Touching a group
 * with the marquee selects the group alone — its children are deliberately
 * left out, since a group carries them when it moves and selecting both
 * would apply the drag delta twice — so without this the whole gesture drew
 * nothing at all. That is the "nodes in the box do not show up on the
 * materialised bounding box" report: there was no box, because one node was
 * selected and the threshold was two.
 *
 * Unmeasured nodes are skipped rather than treated as zero-sized: including
 * one would drag a corner of the box to its position and draw a rectangle
 * round empty canvas.
 */
export function computeSelectionBounds(
  nodes: readonly SelectionBoundsNode[],
  minNodes: number = SELECTION_BOUNDS_MIN_NODES,
): SelectionBounds | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let count = 0
  let sawGroup = false

  for (const node of nodes) {
    if (!node.selected || node.hidden) continue
    if (node.isGroup) sawGroup = true
    const { width, height } = node
    if (width === null || width === undefined || height === null || height === undefined) continue
    const pos = node.positionAbsolute ?? node.position
    count += 1
    minX = Math.min(minX, pos.x)
    minY = Math.min(minY, pos.y)
    maxX = Math.max(maxX, pos.x + width)
    maxY = Math.max(maxY, pos.y + height)
  }

  if (count === 0) return null
  if (count < minNodes && !sawGroup) return null
  return {
    x: minX - PADDING,
    y: minY - PADDING,
    width: maxX - minX + PADDING * 2,
    height: maxY - minY + PADDING * 2,
    count,
  }
}

interface BoundsSnapshot {
  bounds: SelectionBounds | null
  transform: [number, number, number]
  /** A marquee or node drag is in flight — something other than this box. */
  otherDragActive: boolean
}

export function selectBounds(state: {
  getNodes: () => (SelectionBoundsNode & {
    id: string
    parentId?: string | null
    parentNode?: string | null
    dragging?: boolean
  })[]
  transform: [number, number, number]
  userSelectionActive: boolean
}): BoundsSnapshot {
  const nodes = state.getNodes()
  // A group is simply a node other nodes point at. Both spellings, because
  // useYCanvas writes `parentId` while ReactFlow internals still carry
  // `parentNode` on some paths.
  const parentIds = new Set<string>()
  for (const node of nodes) {
    const parent = node.parentId ?? node.parentNode
    if (parent) parentIds.add(parent)
  }
  const marked = nodes.map((node) => ({ ...node, isGroup: parentIds.has(node.id) }))
  return {
    bounds: computeSelectionBounds(marked),
    transform: state.transform,
    otherDragActive: state.userSelectionActive || nodes.some((node) => node.dragging === true),
  }
}

/** Value equality, so panning alone does not re-render when nothing moved. */
export function boundsSnapshotsEqual(a: BoundsSnapshot, b: BoundsSnapshot): boolean {
  if (a.transform[0] !== b.transform[0]) return false
  if (a.transform[1] !== b.transform[1]) return false
  if (a.transform[2] !== b.transform[2]) return false
  if (a.otherDragActive !== b.otherDragActive) return false
  if (a.bounds === null || b.bounds === null) return a.bounds === b.bounds
  return (
    a.bounds.x === b.bounds.x &&
    a.bounds.y === b.bounds.y &&
    a.bounds.width === b.bounds.width &&
    a.bounds.height === b.bounds.height
  )
}

/**
 * Class the canvas already uses to blank node pointer-events during a drag.
 * A box drag is a node drag, so it wears the same class and reuses the same
 * rule in globals.css — otherwise the Config / Variants hover overlay, which
 * is pure CSS `group-hover`, opens under the moving cursor. Pointer capture
 * does not prevent that: it redirects events, it does not clear `:hover`.
 */
const NODE_DRAGGING_CLASS = 'canvas-node-dragging'

/**
 * Whether the box should be on screen at all.
 *
 * It is shown when the gesture ENDS, not while one runs. Two bugs came from
 * drawing it mid-gesture. Redrawing it on every pointer move, as nodes
 * entered and left a sweep, is what flickered — and ReactFlow's own
 * selection rectangle already gives the sweep its feedback. Worse, because
 * the box appears as soon as two nodes are selected, an interactive
 * rectangle materialised under the moving cursor partway through a sweep
 * and swallowed the rest of the gesture, so the selection stopped short of
 * what was swept.
 *
 * This is the ONLY gate: not rendering is what keeps the box out of the
 * way, so there is deliberately no second pointer-events guard to fall out
 * of step with it.
 *
 * Its own drag is the exception — there `otherDragActive` is true precisely
 * because the nodes this box is moving report `dragging`, and unmounting
 * mid-gesture would break the pointer capture the drag relies on.
 */
export function isSelectionBoxVisible(
  isBoxDragging: boolean,
  otherDragActive: boolean,
): boolean {
  return isBoxDragging || !otherDragActive
}

/** A node captured at drag start, with the origin its delta applies to. */
interface DragItemOrigin {
  id: string
  position: { x: number; y: number }
  positionAbsolute: { x: number; y: number }
}

interface BoxDragState {
  pointerId: number
  startX: number
  startY: number
  scale: number
  origins: DragItemOrigin[]
}

/**
 * The selected nodes a box drag should move.
 *
 * A child whose ancestor is also selected is left out: the ancestor carries
 * it, and moving both applies the delta twice. This is the same trap
 * ReactFlow's own `getDragItems` falls into for nodes using the modern
 * `parentId` spelling, which `patches/@reactflow+core+11.11.4.patch` fixes.
 */
export function collectBoxDragOrigins(
  nodes: readonly {
    id: string
    selected?: boolean
    hidden?: boolean
    draggable?: boolean
    parentId?: string
    parentNode?: string
    position: { x: number; y: number }
    positionAbsolute?: { x: number; y: number }
  }[],
): DragItemOrigin[] {
  const selectedIds = new Set(nodes.filter((n) => n.selected && !n.hidden).map((n) => n.id))
  const byId = new Map(nodes.map((n) => [n.id, n]))

  const hasSelectedAncestor = (id: string): boolean => {
    let parent = byId.get(id)?.parentId ?? byId.get(id)?.parentNode
    // Bounded walk: a malformed parent cycle must not hang the pointer.
    for (let depth = 0; parent !== undefined && depth < 32; depth++) {
      if (selectedIds.has(parent)) return true
      const next = byId.get(parent)
      parent = next?.parentId ?? next?.parentNode
    }
    return false
  }

  const origins: DragItemOrigin[] = []
  for (const node of nodes) {
    if (!node.selected || node.hidden) continue
    if (node.draggable === false) continue
    if (hasSelectedAncestor(node.id)) continue
    origins.push({
      id: node.id,
      position: { ...node.position },
      positionAbsolute: { ...(node.positionAbsolute ?? node.position) },
    })
  }
  return origins
}

interface CanvasSelectionBoundsProps {
  /**
   * Right-click on the box. The box sits on top of the selected nodes, so
   * without this a right-click there reaches neither the node nor the pane.
   */
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void
}

export const CanvasSelectionBounds = memo(function CanvasSelectionBounds({ onContextMenu }: CanvasSelectionBoundsProps) {
  const { bounds, transform, otherDragActive } = useStore(selectBounds, boundsSnapshotsEqual)
  const store = useStoreApi()
  const dragRef = useRef<BoxDragState | null>(null)
  const [isBoxDragging, setIsBoxDragging] = useState(false)

  /** Wear the canvas's drag class so node hover chrome stays shut. */
  const setNodeDraggingClass = useCallback((enabled: boolean) => {
    store.getState().domNode?.classList.toggle(NODE_DRAGGING_CLASS, enabled)
  }, [store])

  // Mount inside ReactFlow's zoom pane, not where this component sits in
  // the tree.
  //
  // ReactFlow renders `children` as a SIBLING after GraphView — inside
  // `.react-flow` but outside `.react-flow__renderer`, which is the element
  // d3-zoom binds to. An overlay left there never lets a wheel event reach
  // d3, so the canvas could not be scrolled wherever the box sat under the
  // cursor. Removing the `nopan` class did not help and could not have: the
  // event was not being filtered, it was never arriving.
  //
  // `.react-flow__renderer` is `position: absolute; inset: 0`, the same kind
  // of positioned ancestor the box had before, so its own absolute
  // positioning is unaffected. Portalling to the end of that element's
  // children also keeps it painting above the viewport.
  const [host, setHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    const find = () =>
      store.getState().domNode?.querySelector<HTMLElement>('.react-flow__renderer') ?? null
    const immediate = find()
    if (immediate) {
      setHost(immediate)
      return
    }
    // domNode is assigned in one of ReactFlow's own effects, so on the first
    // render it can still be null. Watch until it appears, then stop.
    const unsubscribe = store.subscribe(() => {
      const next = find()
      if (next) {
        setHost(next)
        unsubscribe()
      }
    })
    return unsubscribe
  }, [store])

  // Stop ReactFlow's d3-zoom seeing a press on the box as the start of a
  // pan. It has to be a NATIVE listener on the element: d3 binds mousedown
  // on the zoom pane, which is an ancestor, so a React handler — delegated
  // at the React root, above .react-flow — would fire after d3 had already
  // started. A listener here runs first on the way up.
  const boxElRef = useRef<HTMLDivElement | null>(null)
  const stopNativeMouseDown = useCallback((event: MouseEvent) => {
    if (event.button === 0) event.stopPropagation()
  }, [])
  const attachBox = useCallback(
    (el: HTMLDivElement | null) => {
      boxElRef.current?.removeEventListener('mousedown', stopNativeMouseDown)
      boxElRef.current = el
      el?.addEventListener('mousedown', stopNativeMouseDown)
    },
    [stopNativeMouseDown],
  )

  /** Apply the pointer delta to every captured node. */
  const applyDelta = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, dragging: boolean) => {
      const drag = dragRef.current
      if (!drag) return
      const dx = (event.clientX - drag.startX) / drag.scale
      const dy = (event.clientY - drag.startY) / drag.scale
      const items: NodeDragItem[] = drag.origins.map((origin) => ({
        id: origin.id,
        position: { x: origin.position.x + dx, y: origin.position.y + dy },
        positionAbsolute: {
          x: origin.positionAbsolute.x + dx,
          y: origin.positionAbsolute.y + dy,
        },
        // ReactFlow only reads `distance` while computing its own drag; the
        // store just forwards these items into position changes.
        distance: { x: 0, y: 0 },
      }))
      store.getState().updateNodePositions(items, true, dragging)
    },
    [store],
  )

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return
      const state = store.getState()
      if (!state.nodesDraggable) return
      const origins = collectBoxDragOrigins(state.getNodes())
      if (origins.length === 0) return

      setIsBoxDragging(true)
      setNodeDraggingClass(true)
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scale: state.transform[2],
        origins,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
      // The pane would otherwise read this as the start of a marquee.
      event.stopPropagation()
      event.preventDefault()
    },
    [store, setNodeDraggingClass],
  )

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      applyDelta(event, true)
    },
    [applyDelta],
  )

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) return
      // Final emission with dragging=false is what marks the drag complete
      // for downstream listeners and persistence.
      applyDelta(event, false)
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      dragRef.current = null
      setIsBoxDragging(false)
      setNodeDraggingClass(false)
    },
    [applyDelta, setNodeDraggingClass],
  )

  if (!host) return null
  if (!bounds) return null

  // Not while a drag is running — the box appears when the gesture ends.
  //
  // During a marquee the box was being redrawn on every pointer move as
  // nodes entered and left the sweep, each frame a different rectangle. That
  // is the flicker; ReactFlow's own selection rectangle is already the
  // feedback for the sweep, so a second rectangle chasing it adds nothing.
  // Same for a node drag, where the box would otherwise trail the nodes.
  //
  // Its own drag is excluded: removing the element mid-gesture would break
  // the pointer capture the drag depends on.
  if (!isSelectionBoxVisible(isBoxDragging, otherDragActive)) return null


  const [translateX, translateY, scale] = transform
  return createPortal(
    <div
      // Mirrors ReactFlow's own container pattern: the wrapper carries the
      // viewport transform and the box is laid out in flow units inside it,
      // so a pan or zoom changes one transform string rather than four
      // recomputed pixel values.
      className="pointer-events-none absolute left-0 top-0 z-[4] origin-top-left"
      style={{ transform: `translate(${translateX}px,${translateY}px) scale(${scale})` }}
      data-testid="canvas-selection-bounds"
      aria-hidden="true"
    >
      <div
        ref={attachBox}
        // Deliberately NOT `nopan`. That class is how you stop ReactFlow
        // panning when an overlay is grabbed, but its zoom filter reads
        //
        //   isWrappedWithClass(event, noPanClassName) &&
        //     (event.type !== 'wheel' || (panOnScroll && event.type === 'wheel' ...))
        //
        // and this canvas sets panOnScroll — so `nopan` also swallows WHEEL
        // events, and the canvas could not be scrolled while the cursor sat
        // inside the box. The native mousedown listener on `attachBox` stops
        // the pan instead, leaving wheel events free to bubble to the pane.
        //
        // Amber (the canvas accent, --primary) at 2px, against white node
        // highlights, so the box and its contents are told apart at a
        // glance. Border width is in flow units, so it thins as you zoom
        // out; hence 2px rather than 1.
        className="nodrag pointer-events-auto absolute cursor-move rounded-lg border-2 border-dashed"
        style={{
          left: bounds.x,
          top: bounds.y,
          width: bounds.width,
          height: bounds.height,
          borderColor: 'var(--primary)',
          backgroundColor: 'rgba(212,160,67,0.10)',
          boxShadow:
            '0 0 0 1px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(0,0,0,0.35), 0 0 24px 6px rgba(212,160,67,0.28)',
        }}
        data-testid="canvas-selection-bounds-handle"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onContextMenu={onContextMenu}
      />
    </div>,
    host,
  )
})
